import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime.ts';

export type NudgeRecommendationFilter = {
	/** True when all global page-level conditions are met (Rovo enabled, page content type, not edit mode, read time loaded, read time >= 1 min). */
	isBaseEligible: boolean;
	/** Read time of the page in minutes, or null if not yet loaded. Exposed for nudges that require a higher threshold than the base 1 min. */
	readTimeMinutes: number | null;
};

/**
 * Hook to determine base eligibility for nudges and expose the raw page read time.
 * Base eligibility includes a 1 min read time minimum. Nudges requiring a higher threshold
 * can use readTimeMinutes directly.
 *
 * @param contentType
 * @param contentId
 * @returns NudgeRecommendationFilter
 */
export const useNudgeRecommendationFilter = (
	contentType: string,
	contentId: string,
): NudgeRecommendationFilter => {
	const { isAdminHubAIEnabled } = useSessionData();
	const pageMode = useGetPageMode();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const { readTime, loading: readTimeLoading } = useReadTime(
		contentId,
		!isAdminHubAIEnabled || pageMode === PageMode.EDIT,
	);

	const readTimeMinutes = readTimeLoading ? null : Number(readTime);

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	const minReadTimeMinutes = fg('rovo_button_proactive_nudge_min_read_time_increase') ? 1.0 : 0.5;

	/**
	 * Base eligibility checks (page-level, shared across all nudges):
	 * 1. Rovo Button must be enabled
	 * 2. Content type must be 'page' (TODO: extend to 'blogpost' when backend 500s are fixed)
	 * 3. Page must not be in edit mode
	 * 4. Read time query must be completed
	 * 5. Read time must exceed the minimum threshold (1 min with gate on, 30s with gate off)
	 */
	const isBaseEligible =
		isRovoButtonEnabled &&
		contentType === 'page' &&
		pageMode !== PageMode.EDIT &&
		readTimeMinutes !== null &&
		readTimeMinutes > minReadTimeMinutes;

	return {
		isBaseEligible,
		readTimeMinutes,
	};
};
